import React, { useLayoutEffect } from "react";
import styles from "./buttonSet.module.scss";
import classnames from "classnames";
import { SvgV2 } from "../SvgV2";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@whatslly/ui-components/Tooltip";
import { TextToolTipCheck } from "@whatslly/ui-components/TextToolTipCheck";

export interface ButtonSetItem {
  key: string;
  value: React.ReactNode;
  disabled?: boolean;
  testId?: string;
}

export type ItemType<T> = T & { testId?: string } & (
    | { locked?: false; lockedToolTip?: undefined }
    | {
        locked: true;
        lockedToolTip: string;
      }
  );

export interface ButtonSetProps<T extends ButtonSetItem> {
  items: ItemType<T>[];
  onClick?: (item: T) => void;
  activeItem?: T;
  className?: string;
  activeClassName?: string;
  itemClassName?: string;
  disabled?: boolean;
  testId?: string;
}

export const ButtonSet = <T extends ButtonSetItem>({
  items,
  onClick,
  activeItem,
  className,
  activeClassName,
  itemClassName,
  disabled,
  testId,
}: ButtonSetProps<T>) => {
  const divRef = React.useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const active = items.findIndex((x) => x === activeItem);
    const activeIndicator = divRef.current?.querySelector<HTMLSpanElement>(`.${styles.activeItem}`);
    if (!divRef.current || !activeIndicator) {
      return;
    }
    const { left: base } = divRef.current.getBoundingClientRect();
    const { left, width } = divRef.current.children[active + 1].getBoundingClientRect();
    activeIndicator.style.left = `${left - base}px`;
    activeIndicator.style.width = `${width}px`;
  }, [activeItem, items]);

  return (
    <div data-testid={testId} className={classnames(styles.ButtonSet, className)} ref={divRef}>
      <span className={classnames(styles.activeItem, activeClassName)}>&nbsp;</span>
      {items.map((item) => (
        <Item key={item.key} item={item} onClick={onClick} disabled={disabled} itemClassName={itemClassName} />
      ))}
    </div>
  );
};

const LockTooltip: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className={styles.lockedTooltipWrapper}>
      <div className={styles.lockedTooltipHeader}>{t("chat_component.premium_feature_header")}</div>
      <div className={styles.lockedTooltipText}>
        <span>{`${t("chat_component.premium_feature_text")} `}</span>
        <a className={styles.lockedTooltipSalesLink} target="_blank" href={"mailto:support@tuvis.com"}>
          {t("chat_component.premium_feature_contact_sales")}
        </a>
      </div>
    </div>
  );
};

export function Item<T extends ButtonSetItem>({
  item,
  onClick,
  disabled,
  itemClassName,
}: {
  item: ItemType<T>;
  onClick?: (item: T) => void;
  disabled?: boolean;
  itemClassName?: string;
}): React.JSX.Element {
  return (
    <Tooltip placement={"bottom-right"} value={<LockTooltip />} disabled={!item.locked}>
      <div
        data-testid={item.testId}
        onClick={() => {
          if (!item.locked) {
            onClick?.(item);
          }
        }}
        className={classnames(styles.item, itemClassName, {
          [styles.disabled]: disabled || item.disabled,
        })}
      >
        <TextToolTipCheck className={styles.overflowCheck} text={item.value} />
        {item.locked && (
          <span data-testid="item-locked-icon" className={styles.locked}>
            (<SvgV2 iconName="premium-lock" />)
          </span>
        )}
      </div>
    </Tooltip>
  );
}
